import * as Dialog from '@radix-ui/react-dialog';
import { Button } from '../Button/Button.tsx';
import { useModalLayer } from '../../libs/modalStack.ts';
import type {
  OverlayAccent,
  OverlayDialogProps,
  OverlayTone,
} from '../../types/overlay-dialog.type.ts';

const toneToVariant: Record<OverlayTone, 'primary' | 'danger'> = {
  default: 'primary',
  danger: 'danger',
  success: 'primary',
  warning: 'primary',
};

/**
 * accent → ring 색상 매핑.
 * Tailwind JIT 가 안전하게 잡도록 동적 보간 대신 정적 클래스 문자열로 관리.
 */
const ACCENT_RING: Record<OverlayAccent, string> = {
  brand: 'ring-brand-500',
  'xcost-pro': 'ring-xcost-pro',
  'xpixels-aqc': 'ring-xpixels-aqc',
  'xpixels-sqc': 'ring-xpixels-sqc',
  'xpixels-eqc': 'ring-xpixels-eqc',
  'xpixels-mqc': 'ring-xpixels-mqc',
  'adobe-red': 'ring-adobe-red',
};

export function OverlayDialog({
  kind,
  open,
  title,
  message,
  confirmText,
  cancelText,
  tone = 'default',
  accent = 'brand',
  dismissible = true,
  onResolve,
}: OverlayDialogProps) {
  const isConfirm = kind === 'confirm';
  const confirmLabel = confirmText ?? (isConfirm ? '확인' : '닫기');
  const cancelLabel = cancelText ?? '취소';
  const confirmVariant = toneToVariant[tone];
  const accentRing = ACCENT_RING[accent];
  // 확인창이 떠 있는 동안 아래 모달이 ESC 로 같이 닫히지 않게 한다.
  useModalLayer(open);

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(next) => {
        // dismissible=false 인 경우, 바깥 인터랙션은 onInteractOutside 등에서 막힘
        if (!next) onResolve(false);
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay
          className={
            'fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm ' +
            'data-[state=open]:animate-in data-[state=open]:fade-in-0 ' +
            'data-[state=closed]:animate-out data-[state=closed]:fade-out-0'
          }
        />
        <Dialog.Content
          onEscapeKeyDown={(e) => {
            if (!dismissible) e.preventDefault();
          }}
          onPointerDownOutside={(e) => {
            if (!dismissible) e.preventDefault();
          }}
          onInteractOutside={(e) => {
            if (!dismissible) e.preventDefault();
          }}
          className={
            'fixed left-1/2 top-1/2 z-[60] -translate-x-1/2 -translate-y-1/2 ' +
            'w-[calc(100%-2rem)] max-w-sm rounded-xl bg-white dark:bg-dark-card shadow-2xl ' +
            `ring-2 ${accentRing} ` +
            'data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95 ' +
            'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 ' +
            'focus:outline-none'
          }
        >
          <div className="px-6 pt-6 pb-2">
            {title && (
              <Dialog.Title className="text-base font-semibold text-gray-900 dark:text-neutral-100">
                {title}
              </Dialog.Title>
            )}
            {message && (
              <Dialog.Description className="mt-2 text-sm text-gray-600 dark:text-neutral-400 whitespace-pre-line break-words">
                {message}
              </Dialog.Description>
            )}
          </div>

          <div className="flex justify-end gap-2 px-6 py-4">
            {isConfirm && (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => onResolve(false)}
              >
                {cancelLabel}
              </Button>
            )}
            <Button
              variant={confirmVariant}
              size="sm"
              autoFocus
              onClick={() => onResolve(true)}
            >
              {confirmLabel}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
